import SwiperCore from "swiper";
import debounce from "lodash.debounce";

import { handleActivePanelButtonVisibility } from "./horizontalScrollPanel";

const CLASS_PREFIX = "js-comparator";
const ACTIVE_CLASS = "is-active";
const WITH_SCROLL_PANEL_CLASS = "with-scroll-panel";
/* important styles to overwrite default styles */
const DISPLAY_NONE_CLASS = "!hidden";
const DISPLAY_BLOCK_CLASS = "!block";
const JUSTIFY_START_CLASS = "!justify-start";
const JUSTIFY_CENTER_CLASS = "!justify-center";

const INTRO_CSS_MIN_HEIGHT_VAR = "--intro-min-height";

const SLIDE_TRANSITION_DURATION_MS = 300;

/**
 * Handle comparator behaviour
 *
 * @param container {HTMLElement} container element
 */
const Comparator = (container: HTMLElement): void => {
  const swiperContainer: HTMLDivElement = container.querySelector(`.${CLASS_PREFIX}-swiper`);

  const panelContainer = container.querySelector(`.${CLASS_PREFIX}-panel-container`);
  const contentContainer = container.querySelector(`.${CLASS_PREFIX}-content-container`);

  const comparatorButtonListEl: HTMLUListElement = container.querySelector(
    `.${CLASS_PREFIX}-button-list`
  );
  const panelButtonArr: HTMLButtonElement[] = Array.from(
    comparatorButtonListEl.querySelectorAll(`.${CLASS_PREFIX}-panel-button`)
  );

  const swiperWrapper: HTMLUListElement = contentContainer.querySelector(
    `.${CLASS_PREFIX}-swiper-wrapper`
  );
  const contentItemArr: HTMLLIElement[] = Array.from(
    contentContainer.querySelectorAll(`.${CLASS_PREFIX}-swiper-slide`)
  );

  const itemIntroArr: HTMLDivElement[] = Array.from(
    contentContainer.querySelectorAll(`.${CLASS_PREFIX}-item-intro`)
  );

  /* Helper variable for changing active button classes */
  let activeButtonEl: HTMLButtonElement = null;
  let isButtonClicked = false;

  /* Swiper init */
  const swiper = new SwiperCore(swiperContainer, {
    enabled: true,
    speed: SLIDE_TRANSITION_DURATION_MS,
    slidesPerView: "auto",
    longSwipesRatio: 0.2,
    loop: false,
    resizeObserver: true,
    // Disable scroll bounce effect at beggining and at the end of container
    resistanceRatio: 0,
  });

  /**
   * Change panel button active classes on event
   */
  const changePanelActiveButton = (newActiveButton: HTMLButtonElement): void => {
    /* Remove classlist from previous active button */
    activeButtonEl.classList.remove(ACTIVE_CLASS);

    /* Add class to new active button */
    activeButtonEl = newActiveButton;
    activeButtonEl.classList.add(ACTIVE_CLASS);
  };

  /**
   * Handle swiper slide change event
   *
   * Change panel active button style and visibility
   */
  swiper.on("slideChange", (swiperEvent): void => {
    const { realIndex: swiperActiveIndex } = swiperEvent;
    const activeButtonIndex = panelButtonArr.findIndex((button) => button === activeButtonEl);

    /**
     * Swipe was called programmatically by panel button click,
     * therefore skip button style change - it was already changed in
     * "handleButtonClick" function
     */
    if (activeButtonIndex === swiperActiveIndex || isButtonClicked) {
      isButtonClicked = false;
      return;
    }

    const newActiveButton = panelButtonArr[swiperActiveIndex];

    /* Toggle panel buttons classes */
    changePanelActiveButton(newActiveButton);

    /* Check panel active button visibility */
    handleActivePanelButtonVisibility({
      containerEl: comparatorButtonListEl,
      itemElement: newActiveButton,
    });
  });

  /**
   * Handle panel button click and slide to clicked item
   */
  const handleButtonClick = (event: MouseEvent): void => {
    const clickedButton = event.currentTarget as HTMLButtonElement;

    if (clickedButton === activeButtonEl) return;

    isButtonClicked = true;

    /* Toggle panel buttons classes */
    changePanelActiveButton(clickedButton);

    const clickedButtonIndex = panelButtonArr.findIndex((button) => button === clickedButton);

    /* Slide to clicked index button */
    swiper.slideTo(clickedButtonIndex, SLIDE_TRANSITION_DURATION_MS);
  };

  /**
   * Remove scroll panel hidden class on scroll panel container
   * and apply class on content container to adjust top margin
   */
  const showScrollPanel = (): void => {
    // Enable swiper
    swiper.enable();

    container.classList.add(WITH_SCROLL_PANEL_CLASS);
    contentContainer.classList.add(WITH_SCROLL_PANEL_CLASS);

    swiperWrapper.classList.remove(JUSTIFY_CENTER_CLASS);
    swiperWrapper.classList.add(JUSTIFY_START_CLASS);

    panelContainer.classList.remove(DISPLAY_NONE_CLASS);
    panelContainer.classList.add(DISPLAY_BLOCK_CLASS);
  };

  /**
   * Apply hidden class on scroll panel container
   * and remove class from content container to adjust top margin
   */
  const hideScrollPanel = (): void => {
    // Disable swiper
    swiper.disable();

    container.classList.remove(WITH_SCROLL_PANEL_CLASS);
    contentContainer.classList.remove(WITH_SCROLL_PANEL_CLASS);

    swiperWrapper.classList.remove(JUSTIFY_START_CLASS);
    swiperWrapper.classList.add(JUSTIFY_CENTER_CLASS);

    panelContainer.classList.remove(DISPLAY_BLOCK_CLASS);
    panelContainer.classList.add(DISPLAY_NONE_CLASS);
  };

  /**
   * Detect if list of items is scrollable
   */
  const detectScollableItems = (): void => {
    const { clientWidth: parentWidth } = swiperWrapper;

    /* Count children width */
    const itemsWidth: number = contentItemArr.reduce((acc, curr) => {
      const { width } = curr.getBoundingClientRect();

      const newWidth = acc + width;

      return newWidth;
    }, 0);

    /* Show scroll panel on items overflow */
    if (Math.floor(itemsWidth) > parentWidth) {
      showScrollPanel();
      return;
    }

    hideScrollPanel();
  };

  const measureIntroHeights = (): void => {
    /* Remove height before measuring */
    swiperWrapper.style.setProperty(INTRO_CSS_MIN_HEIGHT_VAR, "0");

    let heighestIntro = 0;

    /* Get heighest intro */
    itemIntroArr.forEach((intro) => {
      const { height } = intro.getBoundingClientRect();

      const roundedHeight = Math.ceil(height);

      if (roundedHeight > heighestIntro) {
        heighestIntro = roundedHeight;
      }
    });

    /* Set heighest intro variable to make all intros same height */
    swiperWrapper.style.setProperty(INTRO_CSS_MIN_HEIGHT_VAR, `${heighestIntro}px`);
  };

  const prepareDefaultState = (): void => {
    [activeButtonEl] = panelButtonArr;
    activeButtonEl.classList.add(ACTIVE_CLASS);

    // Apply click listener on every panel button
    panelButtonArr.forEach((button) => {
      button.addEventListener("click", handleButtonClick);
    });

    detectScollableItems();
    measureIntroHeights();
  };

  // Handle resize event
  window.addEventListener(
    "resize",
    debounce(() => {
      detectScollableItems();
      measureIntroHeights();
    }, 200)
  );

  prepareDefaultState();
};

export default Comparator;
